/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuInfo } from 'react-icons/lu';

import { DATE_BASIS_OPTIONS } from '../constants';
import { pluralize } from '../utils/format';
import PeriodChips from './PeriodChips';
import {
   DateField,
   DateInput,
   FieldError,
   FilterCard,
   FilterGroup,
   FilterHint,
   FilterLabel,
   FilterLabelRow,
   FilterRow,
   InfoBubble,
   InfoHint,
   Segmented,
   SegmentedButton,
} from '../styles';
import MultiSelectDropdown from '../../../../components/MultiSelectDropdown';

// ---------------------------------------------------------------------------
// [B] Barra de filtros.
//
// Linha 1: período (presets + datas livres) e a LENTE de vencimento.
// Linha 2: origem (dropdown multi-select).
//
// ⚠️ NÃO há filtro por CNPJ emissor aqui, embora o design o previsse a partir
// da 2ª conta recebedora (que já existe). Decisão do Caio em 2026-08-08: ver a
// situação por banco não é prioridade, e a aba CNPJ da dobra 4 já responde
// melhor — ela mostra as contas LADO A LADO, enquanto o filtro mostraria uma
// por vez. Para reativar: `bankAccountIds` segue vivo no hook de filtros e no
// service; falta só o grupo de controle aqui.
//
// ⚠️ PENDENTE — chip "Sem classificação" no grupo de origem.
//
// O contrato já o suporta: o backend passou a aceitar o token `none` no CSV de
// `origin_id` (handoff 0086 §2.2), e a camada de service e a leitura da URL já
// o preservam. Falta SÓ o chip aqui, e ele está segurado de propósito: o 0086
// está em DEV. Em produção, `none` ainda é token desconhecido e seria
// descartado em silêncio — o chip acenderia e a resposta viria com a base
// inteira, o filtro fantasma da navegacao.md §1.0.3, agora causado por nós.
//
// Ligar quando o 0086 subir: mais um item no `origins.map` abaixo, com
// `UNCLASSIFIED_TOKEN` no lugar do id. Nada mais muda.
//
// (O "Sem classificação" da dobra de breakdown é outra coisa: vem pronto no
// payload, como linha própria.)
//
// Os atalhos de período moram em `PeriodChips` — a barra fixa que desce ao
// rolar usa os mesmos, com outra aparência.
// ---------------------------------------------------------------------------

export default function FiltersBar({
   filters,
   referenceDate,
   origins,
   fieldErrors,
   onDateRangeChange,
   onDateBasisChange,
   onOriginsChange,
}) {
   const originOptions = useMemo(
      () => origins.map((origin) => ({ value: origin.id, label: origin.name })),
      [origins],
   );

   const selectedOrigins = filters.originIds.length;

   return (
      <FilterCard as="form" onSubmit={(event) => event.preventDefault()}>
         <FilterRow>
            <FilterGroup>
               <FilterLabel>Período de emissão</FilterLabel>
               <PeriodChips
                  from={filters.from}
                  to={filters.to}
                  referenceDate={referenceDate}
                  onSelect={onDateRangeChange}
               />
            </FilterGroup>

            <DateField>
               <FilterLabel as="label" htmlFor="filter-from">
                  De
               </FilterLabel>
               <DateInput
                  id="filter-from"
                  type="date"
                  value={filters.from}
                  $invalid={Boolean(fieldErrors.from)}
                  aria-invalid={Boolean(fieldErrors.from)}
                  onChange={(event) =>
                     onDateRangeChange({ from: event.target.value })
                  }
               />
               {fieldErrors.from ? (
                  <FieldError>{fieldErrors.from}</FieldError>
               ) : null}
            </DateField>

            <DateField>
               <FilterLabel as="label" htmlFor="filter-to">
                  Até
               </FilterLabel>
               <DateInput
                  id="filter-to"
                  type="date"
                  value={filters.to}
                  $invalid={Boolean(fieldErrors.to)}
                  aria-invalid={Boolean(fieldErrors.to)}
                  onChange={(event) =>
                     onDateRangeChange({ to: event.target.value })
                  }
               />
               {fieldErrors.to ? <FieldError>{fieldErrors.to}</FieldError> : null}
            </DateField>

            <FilterGroup $right>
               {/* O (i) explica o GRUPO, ao lado do rótulo — não dentro dos
                   botões. Assim cada botão volta a ter uma responsabilidade só
                   (trocar a lente) e a ajuda não disputa a área de clique da
                   ação. Uma bolha descrevendo as duas opções também diz mais
                   que duas bolhas isoladas: o que importa é a diferença entre
                   elas, e este controle muda o significado de três seções ao
                   mesmo tempo. */}
               <FilterLabelRow>
                  <FilterLabel id="basis-label">Vencimento</FilterLabel>
                  <InfoHint tabIndex={0} aria-label="O que é a lente de vencimento">
                     <LuInfo size={14} strokeWidth={2} aria-hidden="true" />
                     <InfoBubble role="tooltip">
                        {DATE_BASIS_OPTIONS.map((option) => (
                           <p key={option.key}>
                              <strong>{option.label}</strong> — {option.hint}
                           </p>
                        ))}
                        <p>
                           Não é filtro, é lente: muda adimplência, aging e safra
                           ao mesmo tempo.
                        </p>
                     </InfoBubble>
                  </InfoHint>
               </FilterLabelRow>

               <Segmented role="group" aria-labelledby="basis-label">
                  {DATE_BASIS_OPTIONS.map((option) => (
                     <SegmentedButton
                        key={option.key}
                        type="button"
                        $active={filters.dateBasis === option.key}
                        aria-pressed={filters.dateBasis === option.key}
                        onClick={() => onDateBasisChange(option.key)}
                     >
                        {option.label}
                     </SegmentedButton>
                  ))}
               </Segmented>
            </FilterGroup>
         </FilterRow>

         <FilterRow>
            <FilterGroup>
               <FilterLabel id="origin-label">Origem</FilterLabel>
               <MultiSelectDropdown
                  label="Origens"
                  emptyLabel="Todas as origens"
                  searchPlaceholder="Buscar origem..."
                  options={originOptions}
                  selected={filters.originIds}
                  onChange={onOriginsChange}
               />
               {fieldErrors.origin_id ? (
                  <FieldError>{fieldErrors.origin_id}</FieldError>
               ) : null}
            </FilterGroup>

            <FilterHint>
               {selectedOrigins
                  ? `${selectedOrigins} de ${pluralize(origins.length, 'origem', 'origens')} · a carga histórica não tem origem e fica de fora`
                  : `${pluralize(origins.length, 'origem', 'origens')} · nenhuma selecionada = todas`}
            </FilterHint>
         </FilterRow>
      </FilterCard>
   );
}

FiltersBar.propTypes = {
   filters: PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
      dateBasis: PropTypes.string,
      // Lista de ids que também aceita o token 'none' (sem classificação).
      originIds: PropTypes.arrayOf(
         PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ),
   }).isRequired,
   referenceDate: PropTypes.string,
   origins: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
   ).isRequired,
   fieldErrors: PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
      origin_id: PropTypes.string,
   }).isRequired,
   onDateRangeChange: PropTypes.func.isRequired,
   onDateBasisChange: PropTypes.func.isRequired,
   onOriginsChange: PropTypes.func.isRequired,
};
